import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT, FONT_STACK } from '@/game/config';
import { SUPPORTED_LANGS, LANG_FLAG, type Lang } from '@/i18n';
import { addRoundedLabelBg } from '@/game/ui/roundedLabelBg';
import { getActivePack, packKey } from '@/game/packs';
import { trackLessonFinished } from '@/analytics';
import { createDailyLifePlanet } from '@/game/planet';
import { STAR_COUNT } from '@/game/progression/config';
import { starGlyphsFor } from '@/game/starGlyphs';
import { maybeShowBuyPrompt } from '@/game/ui/buyPrompt';
import { playJourneyFanfare } from '@/game/audio';
import { ReadingProgression, type SessionSummary } from '@/game/progression/ReadingProgression';

interface CelebrationInit { score: number; summary?: SessionSummary | null }

/**
 * Full-screen "you completed the journey" celebration, shown in place of the normal Summary
 * screen once every word of the active lesson is mastered (the ABC Planet is reached).
 *
 * Reached from whichever exit comes first — RoundScene.endRound(), RoundScene.backToMenu(), or
 * MenuScene on the next launch — because the planet is stamped mid-round and the child may leave
 * by a route that has no end-of-round screen. Showing it settles the debt
 * (markCelebrationShown), so it appears exactly once however it was reached. The `?planet` dev
 * flag forces it on every game-over for testing.
 */
export class CelebrationScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Celebration' });
  }

  create(data: CelebrationInit): void {
    this.input.setDefaultCursor('default');
    const cx = GAME_WIDTH / 2;

    // Settle the debt first thing: whatever happens next (a crash, the app being killed, a stray
    // tap straight to the menu), the child has now seen their celebration.
    const lesson = getActivePack();
    const progression = new ReadingProgression(lesson.lang, lesson);
    // Web demo only. Asked BEFORE the debt is settled, and of the progression rather than of this
    // screen: `?planet` opens the celebration on any game-over, so keying the event on "the screen
    // appeared" would report a lesson finished that is nowhere near it.
    const finished = progression.celebrationPending();
    progression.markCelebrationShown();
    if (finished) trackLessonFinished(packKey(lesson));

    // Warm radial glow behind everything to set the festive tone.
    this.add.rectangle(cx, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x140e2e).setAlpha(0.9);

    // The fanfare starts with the star rain, not after it — the sound is what makes the screen
    // land, so it must not trail the visuals.
    playJourneyFanfare();

    // A rain of stars that keeps falling for the life of the scene.
    this.time.addEvent({ delay: 180, loop: true, callback: () => this.dropConfetti() });
    for (let i = 0; i < 12; i++) this.time.delayedCall(i * 60, () => this.dropConfetti());

    // The journey trophy + caption come from the active lesson's reward (ABC Star by default, the
    // Unicorn Star for the fables journey). See Pack.reward / specs/fables-lesson.md §4.
    const reward = lesson.reward;
    const rewardIcon = reward?.icon ?? 'abc_star';
    const rewardLabelKey = reward?.labelKey ?? 'journey.planetReached';

    // The journey trophy: the procedural earth planet (full city), the generated logo if loaded,
    // else a labelled disc.
    const trophyY = GAME_HEIGHT / 2 - 120;
    let trophy: Phaser.GameObjects.GameObject & { setScale(s: number): unknown };
    if (rewardIcon === 'earth_planet') {
      const planet = createDailyLifePlanet(this, cx, trophyY, 90);
      planet.setStars(STAR_COUNT, STAR_COUNT);   // journey complete → the whole city
      trophy = planet.container;
    } else if (this.textures.exists(rewardIcon)) {
      const img = this.add.image(cx, trophyY, rewardIcon).setOrigin(0.5);
      img.setDisplaySize(180, 180);
      trophy = img;
    } else {
      const c = this.add.container(cx, trophyY);
      const disc = this.add.circle(0, 0, 90, 0x8b6fe0).setStrokeStyle(4, 0xffffff, 0.85);
      const label = this.add.text(0, 0, 'ABC', {
        fontFamily: FONT_STACK, fontSize: '44px', color: '#ffffff',
      }).setOrigin(0.5);
      c.add([disc, label]);
      trophy = c;
    }
    // Gentle pulse + sway so the trophy feels alive.
    this.tweens.add({ targets: trophy, scale: 1.08, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    this.tweens.add({ targets: trophy, angle: { from: -5, to: 5 }, duration: 1300, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });

    const title = this.add.text(cx, GAME_HEIGHT / 2 + 30, i18next.t('celebration.title'), {
      fontFamily: FONT_STACK, fontSize: '52px', color: '#ffd166', align: 'center',
    }).setOrigin(0.5);
    this.tweens.add({ targets: title, scale: 1.06, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });

    this.add.text(cx, GAME_HEIGHT / 2 + 90, i18next.t(rewardLabelKey), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#7ec4ff', align: 'center',
      wordWrap: { width: GAME_WIDTH - 200 },
    }).setOrigin(0.5);

    // The flag of the language whose journey was completed — a glanceable badge of which
    // alphabet the player just mastered.
    const lang = (SUPPORTED_LANGS as readonly string[]).includes(i18next.language)
      ? (i18next.language as Lang) : 'de';
    this.add.text(cx, GAME_HEIGHT / 2 + 140, LANG_FLAG[lang], {
      fontFamily: FONT_STACK, fontSize: '56px',
    }).setOrigin(0.5);

    // Only when there IS a score to report. The two in-round exits hand over the round's real
    // score, but MenuScene's backstop passes 0 — it fires on a *later launch*, when no round just
    // ended — and "Score: 0" under "You did it!" reads as a mark out of ten for finishing the
    // whole journey. Omitting the line leaves the trophy and the flag to carry the screen.
    if (data?.score) {
      this.add.text(cx, GAME_HEIGHT / 2 + 190, `${i18next.t('hud.score')}: ${data.score}`, {
        fontFamily: FONT_STACK, fontSize: '26px', color: '#ffffff',
      }).setOrigin(0.5);
    }

    // Menu is the primary action here — a prominent filled pill on top. Playing
    // again is the secondary option, plain text below.
    const menu = this.add.text(cx, GAME_HEIGHT / 2 + 240, i18next.t('nav.menu'), {
      fontFamily: FONT_STACK, fontSize: '32px', color: '#0a1018', fontStyle: 'bold',
      padding: { x: 28, y: 14 },
    }).setOrigin(0.5).setDepth(60).setInteractive({ useHandCursor: true });
    addRoundedLabelBg(this, menu, { color: 0x7ec4ff, radius: 18 });
    menu.on('pointerdown', () => this.scene.start('Menu'));

    const retry = this.add.text(cx, GAME_HEIGHT / 2 + 300, i18next.t('summary.retry'), {
      fontFamily: FONT_STACK, fontSize: '24px', color: '#aabbcc',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    retry.on('pointerdown', () => this.scene.start('Round'));

    this.input.keyboard?.on('keydown-ESC', () => this.scene.start('Menu'));

    // Web only, and late — a completed journey is the strongest moment to ask, but the
    // celebration gets to land first. No rating ask here: this screen is rare, and the apps'
    // rating trigger is score-based (see src/game/rating.ts).
    maybeShowBuyPrompt(this, { delayMs: 3500 });
  }

  /** Drops one wiggling star glyph from above the screen, fading as it falls. */
  private dropConfetti(): void {
    const x = Phaser.Math.Between(40, GAME_WIDTH - 40);
    const size = Phaser.Math.Between(20, 40);
    const colors = ['#ffd166', '#7ec4ff', '#ff6b9d', '#9b8cff', '#5ad6a0'];
    // The lesson's own glyph: ★ everywhere, spiders for Halloween (a spider needs no colour of its own).
    const glyphs = starGlyphsFor(getActivePack());
    const star = this.add.text(x, -40, glyphs.full, {
      fontFamily: FONT_STACK, fontSize: `${size}px`, color: colors[Phaser.Math.Between(0, colors.length - 1)],
    }).setOrigin(0.5).setDepth(50);
    if (glyphs.halo) star.setShadow(0, 0, glyphs.halo, 10, true, true);
    const wiggle = this.tweens.add({ targets: star, angle: { from: -25, to: 25 }, duration: 220, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    this.tweens.add({
      targets: star, y: GAME_HEIGHT + 60, alpha: { from: 1, to: 0.2 },
      duration: Phaser.Math.Between(2600, 4200), ease: 'Quad.easeIn',
      // The wiggle repeats forever and a destroyed game object does NOT take its tweens with it —
      // Phaser leaves them running against the dead target. At 5.5 stars a second that grew the
      // tween list for as long as the screen was open, so the rain got heavier to compute the
      // longer a child looked at their trophy. Killing it here bounds the list to what is falling.
      onComplete: () => { wiggle.remove(); star.destroy(); },
    });
  }
}
